// src/utils/operationalWriteGate.js
//
// Pass 57A (item A) — NORMALISED operational write policy. V1 is a real operational system: an
// AUTHORISED owner/admin/ops action is LIVE in production, gated by real AUTHORIZATION (role + org
// scope) — not by a build-time "everything read-only" flag. Firestore rules remain the security
// boundary; this is the app-layer authorization + a fail-closed assert so bypassing a React button
// cannot write without an authorised actor.
//
// Back-compat: callers that pass an actor context `{ userData, organizationId }` are authorized by
// role; callers that pass none fall back to the legacy activation flag (emulator / explicit flag), so
// existing tests and un-migrated callers keep working. Tests may still force via __setCaseWriteOverride.

import { canManageOrganization, canOperateOrganization } from './platformAccess';

let testOverride = null; // tests set this explicitly; null → resolve from context/runtime
export function __setCaseWriteOverride(v) { testOverride = v; }

// Actions OPS may perform (day-to-day scheduling). Everything else (team_access/role/invite/revoke,
// visibility policy, category, capability, billing) requires owner/admin. Backend syncNow's allowed
// role list mirrors ORG_OPERATE_ROLES — kept in parity by operationalWriteGate.parity.test.js.
export const OPS_ACTIONS = new Set(['assignment', 'new_booking', 'booking_case', 'source_intake', 'cancellation', 'publish_early', 'add_work', 'sync']);

// THE authoritative authorization check for an operational write.
export function authorizeOrgWrite({ userData = null, organizationId = '', action = 'booking_case' } = {}) {
  if (!userData || !organizationId) return { ok: false, reason: 'missing_actor_or_org' };
  const allowed = OPS_ACTIONS.has(action)
    ? canOperateOrganization(userData, organizationId)  // owner/admin/ops
    : canManageOrganization(userData, organizationId);  // owner/admin only (security/admin)
  return { ok: !!allowed, reason: allowed ? '' : 'not_authorized_for_org' };
}

// Fail-closed assert for any operational write (assignment / add-work / roster / etc.). Throws when the
// actor is not authorised for the org. Test override still forces (back-compat).
export function assertOrgWriteAuthorized({ userData = null, organizationId = '', action = 'booking_case' } = {}) {
  if (testOverride === true) return;
  const r = authorizeOrgWrite({ userData, organizationId, action });
  if (!r.ok) { const e = new Error(`Not authorised for this ${action} (${r.reason}).`); e.code = 'ORG_WRITE_UNAUTHORIZED'; throw e; }
}

// Pass 57C-final (item 3): a production operational write MUST carry an actor context — a caller may
// not omit it. Missing actor fails CLOSED in production; a test/emulator run (jest NODE_ENV==='test',
// the emulator flag, or an explicit test override) may proceed unauthenticated for fixtures only.
const isTestOrEmulator = () => testOverride === true || process.env.NODE_ENV === 'test' || process.env.REACT_APP_USE_EMULATOR === 'true';
export function requireOrgWriteActor({ userData = null, organizationId = '', action = 'booking_case' } = {}) {
  if (userData) { assertOrgWriteAuthorized({ userData, organizationId, action }); return; }
  if (isTestOrEmulator()) return; // fixtures/emulator only
  const e = new Error(`Actor context required for this ${action} write.`); e.code = 'ORG_WRITE_ACTOR_REQUIRED'; throw e;
}

const legacyFlag = () => process.env.REACT_APP_USE_EMULATOR === 'true' || process.env.REACT_APP_ACTIVATE_CASE_WRITES === 'true';

// Booking-case writes. With an actor context → real authorization; without → legacy flag.
export function canWriteBookingCases(context = null) {
  if (testOverride !== null) return testOverride;
  if (context && context.userData) return authorizeOrgWrite({ ...context, action: context.action || 'booking_case' }).ok;
  return legacyFlag();
}

export const CASE_WRITES_DISABLED_MESSAGE = 'You do not have permission to make this booking change.';

export function assertCanWriteBookingCases(context = null) {
  if (!canWriteBookingCases(context)) throw new Error(CASE_WRITES_DISABLED_MESSAGE);
}

// Team access-management writes (invite/resend/cancel/disable/role/capability/participation).
export function canManageTeamAccess(context = null) {
  if (testOverride !== null) return testOverride;
  if (context && context.userData) return authorizeOrgWrite({ ...context, action: 'team_access' }).ok;
  return legacyFlag();
}
export const TEAM_WRITES_DISABLED_MESSAGE = 'You do not have permission to manage team access.';
export function assertCanManageTeamAccess(context = null) {
  if (!canManageTeamAccess(context)) throw new Error(TEAM_WRITES_DISABLED_MESSAGE);
}
